import {
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { EXTERNAL_REGISTER_SETTINGS_ROUTES } from "../externalRegisterRoutes";
import { ExternalRegisterAdminErrorBoundary } from "./ExternalRegisterAdminErrorBoundary";
import { ExternalRegisterMaintenancePanel } from "./ExternalRegisterMaintenancePanel";
import { ExternalRegisterSettingsPanel } from "./ExternalRegisterSettingsPanel";
import {
  ExternalRegisterAdminProvider,
  useExternalRegisterAdmin,
} from "./externalRegisterAdminProvider";
import type {
  ExternalRegisterAdminService,
  ExternalRegisterDevPurgeResponse,
} from "./externalRegisterAdminTypes";

type ExternalRegisterAdminViewProps = {
  service?: ExternalRegisterAdminService;
  diagnosticsEnabled?: boolean;
};

type AdminTab = "settings" | "maintenance";

const DEV_PURGE_CONFIRMATION = "PURGE_EXTERNAL_REGISTER";
const isDevPurgePanelEnabled = () => import.meta.env.VITE_EXTERNAL_REGISTER_DEV_PURGE_ENABLED === "true";

const AdminCrashTrigger = () => {
  const { t } = useTranslation();
  const [shouldCrash, setShouldCrash] = useState(false);
  if (shouldCrash) {
    throw new Error(t("externalRegister.admin.diagnostics.errorMessage"));
  }

  return (
    <Button size="small" color="warning" variant="outlined" onClick={() => setShouldCrash(true)}>
      {t("externalRegister.admin.buttons.crashDiagnostics")}
    </Button>
  );
};

const AdminHeader = ({
  activeTab,
  onTabChange,
  diagnosticsEnabled,
}: {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  diagnosticsEnabled: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        pt: 3,
        backgroundColor: "background.default",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          borderRadius: 1,
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" noWrap sx={{ fontWeight: 800 }}>
                {t("externalRegister.admin.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {t("externalRegister.admin.description")}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {diagnosticsEnabled ? <AdminCrashTrigger /> : null}
              <Button size="small" variant="outlined" onClick={() => navigate(EXTERNAL_REGISTER_SETTINGS_ROUTES.import)}>
                {t("externalRegister.admin.buttons.openImport")}
              </Button>
              <Button size="small" variant="outlined" onClick={() => navigate(EXTERNAL_REGISTER_SETTINGS_ROUTES.importHistory)}>
                {t("externalRegister.admin.buttons.importHistory")}
              </Button>
            </Stack>
          </Stack>
          <Tabs
            value={activeTab}
            onChange={(_event, value) => onTabChange(value)}
            aria-label={t("externalRegister.admin.tabs.ariaLabel")}
          >
            <Tab value="settings" label={t("externalRegister.admin.tabs.settings")} />
            <Tab value="maintenance" label={t("externalRegister.admin.tabs.maintenance")} />
          </Tabs>
        </Stack>
      </Paper>
    </Box>
  );
};

const ExternalRegisterDevPurgePanel = () => {
  const { t } = useTranslation();
  const { service, refreshEntries } = useExternalRegisterAdmin();
  const [confirmation, setConfirmation] = useState("");
  const [loadingAction, setLoadingAction] = useState<"preview" | "purge" | null>(null);
  const [lastResult, setLastResult] = useState<ExternalRegisterDevPurgeResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runPurge = async (dryRun: boolean) => {
    const confirmationValue = dryRun ? DEV_PURGE_CONFIRMATION : confirmation.trim();
    if (confirmationValue !== DEV_PURGE_CONFIRMATION) {
      return;
    }

    setLoadingAction(dryRun ? "preview" : "purge");
    setErrorMessage(null);
    try {
      const result = await service.purgeExternalRegister({
        confirmation: confirmationValue,
        ...(dryRun ? { dryRun: true } : {}),
      });
      setLastResult(result);
      if (!result.dryRun) {
        await refreshEntries();
      }
    } catch {
      setErrorMessage(t("externalRegister.admin.devPurge.error"));
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1,
        borderColor: "warning.main",
        backgroundColor: "rgba(251, 191, 36, 0.08)",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {t("externalRegister.admin.devPurge.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("externalRegister.admin.devPurge.description")}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={loadingAction !== null}
            onClick={() => void runPurge(true)}
          >
            {loadingAction === "preview" ? t("externalRegister.admin.buttons.previewing") : t("externalRegister.admin.buttons.previewPurge")}
          </Button>
        </Stack>
        <Divider />
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
          <TextField
            size="small"
            label={t("externalRegister.admin.devPurge.label")}
            slotProps={{ htmlInput: { "aria-label": t("externalRegister.admin.devPurge.ariaLabel") } }}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            placeholder={t("externalRegister.admin.devPurge.placeholder")}
            sx={{ minWidth: { md: 340 } }}
          />
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={confirmation !== DEV_PURGE_CONFIRMATION || loadingAction !== null}
            onClick={() => void runPurge(false)}
          >
            {loadingAction === "purge" ? t("externalRegister.admin.buttons.purging") : t("externalRegister.admin.buttons.purgeExternalRegister")}
          </Button>
        </Stack>
        {errorMessage ? (
          <Alert severity="error" variant="outlined">
            {errorMessage}
          </Alert>
        ) : null}
        {lastResult ? (
          <Alert severity={lastResult.dryRun ? "info" : "success"} variant="outlined">
            {lastResult.dryRun
              ? t("externalRegister.admin.devPurge.previewSummary", { entries: lastResult.purgedEntries, patientsCleaned: lastResult.cleanedPatients, patientsScanned: lastResult.scannedPatients })
              : t("externalRegister.admin.devPurge.completedSummary", { entries: lastResult.purgedEntries, patientsCleaned: lastResult.cleanedPatients, patientsScanned: lastResult.scannedPatients })
            }
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
};

const ExternalRegisterAdminContent = ({
  diagnosticsEnabled,
}: {
  diagnosticsEnabled: boolean;
}) => {
  const [activeTab, setActiveTab] = useState<AdminTab>("settings");
  const { errorMessage, clearError } = useExternalRegisterAdmin();

  const content: Record<AdminTab, ReactNode> = useMemo(() => ({
    settings: <ExternalRegisterSettingsPanel />,
    maintenance: <ExternalRegisterMaintenancePanel />,
  }), []);

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflow: "hidden",
          px: 3,
          pb: activeTab === "maintenance" ? 2 : 4,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Stack spacing={2.5} sx={{ flex: 1, minHeight: 0 }}>
          <AdminHeader
            activeTab={activeTab}
            onTabChange={setActiveTab}
            diagnosticsEnabled={diagnosticsEnabled}
          />
          {errorMessage ? (
            <Alert severity="warning" variant="outlined" onClose={clearError}>
              {errorMessage}
            </Alert>
          ) : null}
          {isDevPurgePanelEnabled() ? <ExternalRegisterDevPurgePanel /> : null}
          <Box sx={{ flex: 1, minHeight: 0, overflow: activeTab === "maintenance" ? "hidden" : "auto" }}>
            {content[activeTab]}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export const ExternalRegisterAdminView = ({
  service,
  diagnosticsEnabled = false,
}: ExternalRegisterAdminViewProps) => {
  return (
    <ExternalRegisterAdminErrorBoundary>
      <ExternalRegisterAdminProvider service={service}>
        <ExternalRegisterAdminContent diagnosticsEnabled={diagnosticsEnabled} />
      </ExternalRegisterAdminProvider>
    </ExternalRegisterAdminErrorBoundary>
  );
};

export default ExternalRegisterAdminView;
